/**
 * sync-client.ts — HTTP + SSE sync client for Dumblift.
 *
 * HTTP + SSE sync protocol:
 *   - Push: POST /api/sync/push — send local changes to server
 *   - Pull: POST /api/sync/pull — fetch server changes since lastSyncedAt
 *   - Stream: GET /api/sync/stream — SSE for real-time push notifications
 *
 * Conflict resolution: last-write-wins by updated_at timestamp.
 *
 * Offline handling:
 *   - Local mutations are recorded in _pending_changes via update_hook
 *   - Failed pushes leave changes in _pending_changes
 *   - On reconnect: push all pending, then pull
 */

import type { Database } from './database'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single change record sent to/from the server. */
export interface ChangeRecord {
  table: string
  id: string | number
  data: Record<string, any>
  updated_at: string
  deleted?: boolean
}

/** Response from POST /api/sync/push */
interface PushResponse {
  applied: Array<{ table: string; id: string | number }>
  conflicts: ChangeRecord[]
}

/** Response from POST /api/sync/pull */
interface PullResponse {
  changes: ChangeRecord[]
  syncedAt: string
}

export type SyncStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'syncing'
  | 'error'

export interface SyncStatusListener {
  (status: SyncStatus): void
}

/** All 12 data tables in the schema. */
const DATA_TABLES = [
  'programs',
  'program_workouts',
  'progressions',
  'progression_cells',
  'progression_cell_modifiers',
  'workout_exercises',
  'adhoc_workout_exercises',
  'exercises',
  'training_setups',
  'completed_sets',
  'activity_workouts',
  'notes',
] as const

/** Tables that should NOT be synced (internal metadata). */
const SYNC_META_TABLES = new Set([
  '_sync_state',
  '_pending_changes',
  '_tombstones',
])

/** Primary key column for each table. All TEXT PK tables use "id". */
const PK_COLUMN: Record<string, string> = {}
for (const t of DATA_TABLES) {
  PK_COLUMN[t] = 'id'
}

// ---------------------------------------------------------------------------
// Standalone initial pull — used by DBProvider to hydrate a fresh browser DB
// ---------------------------------------------------------------------------

/**
 * Pull all data from the server for a profile and apply it to the local
 * database. This is a one-shot operation intended for hydrating a fresh
 * (empty) browser database with existing server-side data.
 *
 * Unlike the full SyncClient, this does not set up SSE, push tracking, or
 * any ongoing sync. It's safe to call before the Sync component mounts.
 *
 * Returns the number of changes applied, or -1 if the pull was skipped
 * (offline, no server data, auth failure).
 */
export async function initialPull(
  db: Database,
  profileId: string,
  baseUrl = ''
): Promise<number> {
  if (!navigator.onLine) return -1

  try {
    const res = await fetch(`${baseUrl}/api/sync/pull`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        profileId,
        lastSyncedAt: '1970-01-01T00:00:00.000Z',
      }),
    })

    if (!res.ok) {
      console.warn(
        `[initialPull] pull request failed: ${res.status} ${res.statusText}`
      )
      return -1
    }

    const result: PullResponse = await res.json()

    if (result.changes.length === 0) return 0

    // Apply all changes inside a transaction
    await db.tx(async (tx) => {
      for (const change of result.changes) {
        if (SYNC_META_TABLES.has(change.table)) continue
        if (!DATA_TABLES.includes(change.table as any)) continue

        const pk = PK_COLUMN[change.table]
        if (!pk) continue

        if (change.deleted) continue // skip deletes on initial pull

        const cols = Object.keys(change.data)
        const placeholders = cols.map(() => '?').join(', ')
        const values = cols.map((c) => change.data[c])

        // INSERT OR REPLACE: on a fresh DB there shouldn't be conflicts,
        // but this handles edge cases gracefully.
        await tx.exec(
          `INSERT OR REPLACE INTO "${change.table}" (${cols.map((c) => `"${c}"`).join(', ')}) VALUES (${placeholders})`,
          values
        )
      }
    })

    // Record the sync timestamp so the regular SyncClient picks up from here
    await db.exec(
      `INSERT OR REPLACE INTO "_sync_state" ("key", "value") VALUES ('lastSyncedAt', ?)`,
      [result.syncedAt]
    )
    await db.exec(
      `INSERT OR REPLACE INTO "_sync_state" ("key", "value") VALUES ('lastPushedAt', ?)`,
      [result.syncedAt]
    )

    return result.changes.length
  } catch (err) {
    console.warn('[initialPull] failed:', err)
    return -1
  }
}

// ---------------------------------------------------------------------------
// SyncClient
// ---------------------------------------------------------------------------

export interface SyncClientOptions {
  db: Database
  profileId: string
  /** Base URL for sync API, e.g. '' for same-origin. */
  baseUrl?: string
  /** Called when sync status changes. */
  onStatusChange?: SyncStatusListener
}

export interface SyncClient {
  /** Start syncing — push pending, pull, connect SSE stream. */
  start(): Promise<void>
  /** Stop syncing — close SSE, stop tracking. */
  stop(): void
  /** Current sync status. */
  status: SyncStatus
  /** Force a push of pending changes. */
  push(): Promise<void>
  /** Force a pull from server. */
  pull(): Promise<void>
  /** Force a full sync - resets lastPushedAt and pushes all data. */
  forceFullSync(): Promise<void>
}

export function createSyncClient(options: SyncClientOptions): SyncClient {
  const { db, profileId, baseUrl = '' } = options

  let status: SyncStatus = 'disconnected'
  let eventSource: EventSource | null = null
  let unsubUpdate: (() => void) | null = null
  let pushDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let stopped = false

  // Track whether we're currently inside an applyServerChanges call
  // so the update_hook doesn't record server-applied writes as pending.
  let applyingServerChanges = false

  // ---------------------------------------------------------------------------
  // Status management
  // ---------------------------------------------------------------------------

  function setStatus(s: SyncStatus) {
    status = s
    options.onStatusChange?.(s)
  }

  // ---------------------------------------------------------------------------
  // Pending changes tracking
  // ---------------------------------------------------------------------------

  /**
   * The update_hook on the database fires for every INSERT/UPDATE/DELETE.
   * We use it to record local mutations into _pending_changes so they can
   * be pushed to the server later.
   *
   * wa-sqlite's update_hook gives us (updateType, dbName, tableName, rowid)
   * but our simplified listener interface in database.ts fires with no args.
   * So we need a different approach: we use a more granular hook.
   *
   * Actually, the current database.ts onUpdate() is a no-arg listener.
   * We can't get table/rowid from it. Instead, we'll use a different strategy:
   *
   * On push, we query _pending_changes. To populate _pending_changes, we'll
   * add SQL triggers that insert into _pending_changes on every data table
   * mutation. This is more reliable than JS-level tracking anyway.
   *
   * BUT — adding 36 triggers (INSERT/UPDATE/DELETE x 12 tables) to the schema
   * is heavy. Let's use a simpler approach: the onUpdate listener fires on
   * any mutation. We debounce it and then check what's changed by comparing
   * with what we last pushed. Actually, the simplest reliable approach:
   *
   * We'll enhance the database.ts onUpdate hook to pass table name and rowid.
   * But that requires changing the Database interface which is risky at this stage.
   *
   * Simplest approach that works: when push() is called, we scan all tables
   * for rows where updated_at > lastPushedAt. This is pull-based rather than
   * event-based, but it's simple and correct. The onUpdate listener just
   * triggers a debounced push().
   */

  /**
   * When the local DB is mutated, schedule a push of pending changes.
   * Debounced to avoid hammering the server on rapid mutations.
   */
  function onLocalMutation() {
    console.log('[sync] onLocalMutation called, stopped:', stopped, 'applyingServerChanges:', applyingServerChanges)
    if (stopped || applyingServerChanges) return
    schedulePush()
  }

  function schedulePush() {
    console.log('[sync] schedulePush called, clearing existing timer:', !!pushDebounceTimer)
    if (pushDebounceTimer) clearTimeout(pushDebounceTimer)
    pushDebounceTimer = setTimeout(() => {
      console.log('[sync] Debounce timer fired, calling pushPending()')
      pushPending().catch((err) => {
        console.warn('[sync] push failed (will retry on reconnect):', err)
      })
    }, 500)
  }

  // ---------------------------------------------------------------------------
  // Push
  // ---------------------------------------------------------------------------

  /**
   * Scan all data tables for rows changed since lastPushedAt,
   * then POST them to the server.
   */
  async function pushPending(): Promise<void> {
    if (stopped) return
    if (!navigator.onLine) return

    setStatus('syncing')

    try {
      // Get the last time we successfully pushed
      const lastPushedISO = await getLastPushedAt()
      
      // Convert ISO timestamp to SQLite format for comparison
      // SQLite stores: "2026-05-26 01:19:18" (space, no T, no Z, no millis)
      // JavaScript produces: "2026-05-26T01:16:19.064Z"
      // We need to convert JS format to SQLite format for string comparison to work
      const lastPushed = lastPushedISO.replace('T', ' ').replace(/\.\d{3}Z$/, '')

      // Collect all changed rows across all tables
      const changes: ChangeRecord[] = []

      for (const table of DATA_TABLES) {
        // Fix any rows with empty/NULL updated_at so they get a real timestamp.
        // Without this, such rows match the query every push cycle (the OR
        // clauses always match regardless of lastPushed) causing an infinite
        // push loop.
        await db.exec(
          `UPDATE "${table}" SET "updated_at" = COALESCE(NULLIF("created_at", ''), CURRENT_TIMESTAMP) WHERE "updated_at" IS NULL OR "updated_at" = ''`
        )

        const rows = await db.execO<Record<string, any>>(
          `SELECT * FROM "${table}" WHERE "updated_at" >= ?`,
          [lastPushed]
        )
        for (const row of rows) {
          changes.push({
            table,
            id: row[PK_COLUMN[table]],
            data: row,
            updated_at: row.updated_at,
          })
        }
      }

      console.log('[sync] pushPending: collected', changes.length, 'changes, lastPushedISO:', lastPushedISO, 'lastPushedSQLite:', lastPushed)
      
      // DEBUG: Check what's actually in the tables
      if (changes.length === 0) {
        for (const table of DATA_TABLES) {
          const allRows = await db.execO<Record<string, any>>(
            `SELECT "${PK_COLUMN[table]}" as id, "updated_at" FROM "${table}" ORDER BY "updated_at" DESC LIMIT 2`
          )
          if (allRows.length > 0) {
            console.log(`[sync] DEBUG ${table}:`, allRows.map(r => ({
              id: r.id,
              updated_at: r.updated_at,
              matches: r.updated_at >= lastPushed
            })))
          }
        }
      }
      
      if (changes.length > 0) {
        const sampleChange = changes[0]
        console.log('[sync] Sample change:', { 
          table: sampleChange.table, 
          id: sampleChange.id, 
          updated_at: sampleChange.updated_at,
          comparison: `${sampleChange.updated_at} >= ${lastPushed}` 
        })
      }

      // Also include tombstones created since last push
      // (use the same SQLite-formatted timestamp)
      const tombstones = await db.execO<{
        table_name: string
        row_id: string
        deleted_at: string
      }>(`SELECT * FROM "_tombstones" WHERE "deleted_at" >= ?`, [lastPushed])
      for (const t of tombstones) {
        changes.push({
          table: t.table_name,
          id: t.row_id,
          data: {},
          updated_at: t.deleted_at,
          deleted: true,
        })
      }

      if (changes.length === 0) {
        console.log('[sync] pushPending: no changes to push')
        setStatus('connected')
        return
      }

      const res = await fetch(`${baseUrl}/api/sync/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ profileId, changes }),
      })

      if (!res.ok) {
        throw new Error(`Push failed: ${res.status} ${res.statusText}`)
      }

      const result: PushResponse = await res.json()

      // Apply any conflicts (server had newer data)
      if (result.conflicts.length > 0) {
        await applyChanges(result.conflicts)
      }

      // Update lastPushedAt to now
      const now = new Date().toISOString()
      await db.exec(
        `INSERT OR REPLACE INTO "_sync_state" ("key", "value") VALUES ('lastPushedAt', ?)`,
        [now]
      )

      setStatus('connected')
    } catch (err) {
      console.error('[sync] push error:', err)
      setStatus('error')
      throw err
    }
  }

  // ---------------------------------------------------------------------------
  // Pull
  // ---------------------------------------------------------------------------

  async function pullChanges(): Promise<void> {
    if (stopped) return
    if (!navigator.onLine) return

    setStatus('syncing')

    try {
      const lastSynced = await getLastSyncedAt()

      const res = await fetch(`${baseUrl}/api/sync/pull`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ profileId, lastSyncedAt: lastSynced }),
      })

      if (!res.ok) {
        throw new Error(`Pull failed: ${res.status} ${res.statusText}`)
      }

      const result: PullResponse = await res.json()

      if (result.changes.length > 0) {
        await applyChanges(result.changes)
      }

      // Update lastSyncedAt
      await db.exec(
        `INSERT OR REPLACE INTO "_sync_state" ("key", "value") VALUES ('lastSyncedAt', ?)`,
        [result.syncedAt]
      )

      setStatus('connected')
    } catch (err) {
      console.error('[sync] pull error:', err)
      setStatus('error')
      throw err
    }
  }

  // ---------------------------------------------------------------------------
  // Apply server changes to local DB
  // ---------------------------------------------------------------------------

  /**
   * Apply changes from the server to the local database.
   * Uses last-write-wins: only apply if server's updated_at > local updated_at.
   * Sets applyingServerChanges flag so the update_hook doesn't re-queue these
   * as pending local changes.
   */
  async function applyChanges(changes: ChangeRecord[]): Promise<void> {
    applyingServerChanges = true
    try {
      await db.tx(async (tx) => {
        for (const change of changes) {
          if (SYNC_META_TABLES.has(change.table)) continue
          if (!DATA_TABLES.includes(change.table as any)) continue

          const pk = PK_COLUMN[change.table]
          if (!pk) continue

          if (change.deleted) {
            // Delete the row locally
            await tx.exec(`DELETE FROM "${change.table}" WHERE "${pk}" = ?`, [
              change.id,
            ])
            // Record tombstone locally
            await tx.exec(
              `INSERT OR REPLACE INTO "_tombstones" ("table_name", "row_id", "deleted_at") VALUES (?, ?, ?)`,
              [change.table, String(change.id), change.updated_at]
            )
            continue
          }

          // Check if we have this row locally
          const existing = await tx.exec(
            `SELECT "updated_at" FROM "${change.table}" WHERE "${pk}" = ?`,
            [change.id]
          )

          if (existing.length > 0) {
            // Row exists — only update if server's updated_at is newer
            const localUpdatedAt = existing[0][0] as string
            if (change.updated_at > localUpdatedAt) {
              // Build UPDATE SET clause from data
              const cols = Object.keys(change.data).filter((c) => c !== pk)
              if (cols.length === 0) continue
              const setClause = cols.map((c) => `"${c}" = ?`).join(', ')
              const values = cols.map((c) => change.data[c])
              values.push(change.id)
              await tx.exec(
                `UPDATE "${change.table}" SET ${setClause} WHERE "${pk}" = ?`,
                values
              )
            }
            // If local is newer, skip (local wins)
          } else {
            // Row doesn't exist locally — insert it
            const cols = Object.keys(change.data)
            const placeholders = cols.map(() => '?').join(', ')
            const values = cols.map((c) => change.data[c])
            await tx.exec(
              `INSERT INTO "${change.table}" (${cols.map((c) => `"${c}"`).join(', ')}) VALUES (${placeholders})`,
              values
            )
          }
        }
      })
    } finally {
      applyingServerChanges = false
    }
  }

  // ---------------------------------------------------------------------------
  // SSE stream
  // ---------------------------------------------------------------------------

  function connectStream() {
    if (stopped) return
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }

    setStatus('connecting')

    const url = `${baseUrl}/api/sync/stream?profileId=${encodeURIComponent(profileId)}`
    eventSource = new EventSource(url, { withCredentials: true })

    eventSource.onopen = () => {
      setStatus('connected')
    }

    eventSource.addEventListener('change', () => {
      // Server notified us that another client pushed changes.
      // Pull those changes.
      pullChanges().catch((err) => {
        console.warn('[sync] pull after SSE change notification failed:', err)
      })
    })

    eventSource.onerror = () => {
      // EventSource auto-reconnects. Just update status.
      if (!stopped) {
        setStatus('error')
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Sync state helpers
  // ---------------------------------------------------------------------------

  async function getLastSyncedAt(): Promise<string> {
    const rows = await db.execO<{ value: string }>(
      `SELECT "value" FROM "_sync_state" WHERE "key" = 'lastSyncedAt'`
    )
    return rows.length > 0 ? rows[0].value : '1970-01-01T00:00:00.000Z'
  }

  async function getLastPushedAt(): Promise<string> {
    const rows = await db.execO<{ value: string }>(
      `SELECT "value" FROM "_sync_state" WHERE "key" = 'lastPushedAt'`
    )
    return rows.length > 0 ? rows[0].value : '1970-01-01T00:00:00.000Z'
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  const client: SyncClient = {
    get status() {
      return status
    },

    async start() {
      stopped = false

      // Subscribe to local mutations
      unsubUpdate = db.onUpdate(onLocalMutation)

      if (!navigator.onLine) {
        setStatus('disconnected')
        // Listen for online event to start sync
        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)
        return
      }

      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)

      try {
        // Initial sync: push pending, then pull
        await pushPending()
        await pullChanges()
        // Connect SSE for real-time updates
        connectStream()
      } catch (err) {
        console.warn(
          '[sync] initial sync failed, will retry on reconnect:',
          err
        )
        setStatus('error')
      }
    },

    stop() {
      stopped = true
      setStatus('disconnected')

      if (unsubUpdate) {
        unsubUpdate()
        unsubUpdate = null
      }

      if (eventSource) {
        eventSource.close()
        eventSource = null
      }

      if (pushDebounceTimer) {
        clearTimeout(pushDebounceTimer)
        pushDebounceTimer = null
      }

      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    },

    async push() {
      await pushPending()
    },

    async pull() {
      await pullChanges()
    },

    async forceFullSync() {
      // Reset lastPushedAt to epoch so all data gets pushed
      await db.exec(
        `INSERT OR REPLACE INTO "_sync_state" ("key", "value") VALUES ('lastPushedAt', ?)`,
        ['1970-01-01T00:00:00.000Z']
      )
      // Push all data
      await pushPending()
      // Pull any server changes
      await pullChanges()
    },
  }

  // ---------------------------------------------------------------------------
  // Online/offline handlers
  // ---------------------------------------------------------------------------

  function handleOnline() {
    if (stopped) return
    // Reconnect: push pending changes, pull updates, reconnect SSE
    pushPending()
      .then(() => pullChanges())
      .then(() => connectStream())
      .catch((err) => {
        console.warn('[sync] reconnect failed:', err)
        setStatus('error')
      })
  }

  function handleOffline() {
    if (stopped) return
    setStatus('disconnected')
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  }

  return client
}
